import { z } from 'zod';
import { ModuleTypeEnum } from './module-type';
import { RecordPermissionPrincipalTypeEnum } from './record-permissions';

// Enum for Custom Field Types, matching Prisma schema
// User-friendly field types for custom field creation
export const FieldTypeEnum = z.enum([
  // Text fields
  'STRING',        // Short text (single line)
  'TEXT',          // Paragraph/multi-line text
  'RICH_TEXT',     // Rich text editor

  // Number fields
  'NUMBER',        // Integer or decimal
  'PERCENTAGE',    // 0-100 with progress bar display
  'CURRENCY',      // Monetary value with currency formatting

  // Date/Time fields
  'DATE',          // Date only
  'DATETIME',      // Date and time
  'TIME',          // Time only

  // Selection fields
  'DROPDOWN',      // Single select from options
  'MULTISELECT',   // Multi select from options
  'BOOLEAN',       // Yes/No toggle

  // Reference fields
  'REFERENCE',     // Link to single record (replaces MODULE_SEARCH)
  'MULTI_REFERENCE', // Link to multiple records
  'USER',          // Link to single user/person
  'MULTI_USER',    // Link to multiple users/people

  // File fields
  'FILE',          // Single file upload (for logos, avatars)
  'ATTACHMENTS',   // Multiple file attachments of any type

  // Other fields
  'LINK',          // URL link

  // Calculated fields (read-only, computed — never stored)
  'FORMULA',  // expression over sibling fields; config in settings.formula
  'LOOKUP',   // value pulled from a joined record; config in settings.lookup
  'ROLLUP',   // aggregate over related records; config in settings.rollup

  // AI-resolved. The result IS stored (an LLM call is expensive and
  // non-deterministic, so it must not re-run on every read); config in
  // settings.ai. See ai-field.ts.
  'AI',

  // Legacy (kept for backward compatibility)
  'MODULE_SEARCH', // @deprecated - use REFERENCE instead
]);
export type FieldTypeEnum = z.infer<typeof FieldTypeEnum>;

// Define dropdown value type
export interface DropdownValue {
  value: string;
  label?: string;
}

// Reference value types with cached names for efficient display
export interface ReferenceValue {
  type: string;    // Module type: ENTITY, PROJECT, TASK
  id: string;      // Module ID (UUID)
  name: string;    // Cached display name
}

export interface UserReferenceValue {
  id: string;      // User ID (UUID)
  name: string;    // Cached display name
}

// Single file value type for FILE field
export interface SingleFileValue {
  documentId: string;    // Document record UUID
  fileName: string;      // Display name
  fileSize: number;      // Size in bytes
  contentType: string;   // MIME type (e.g., 'application/pdf', 'image/jpeg')
}

// Attachment value type for ATTACHMENTS field
export interface AttachmentItem {
  documentId: string;    // Document record UUID
  fileName: string;      // Display name
  fileSize: number;      // Size in bytes
  contentType: string;   // MIME type (e.g., 'application/pdf', 'image/jpeg')
}

export interface AttachmentsValue {
  attachments: AttachmentItem[];
}

// Define custom field value type for form inputs
export type CustomFieldInputValue = string | number | boolean | Date | DropdownValue | null;
export type CustomFieldsInputRecord = Record<string, CustomFieldInputValue>;

export const CustomFieldPermissionTargetSchema = z.object({
  principalType: z.nativeEnum(RecordPermissionPrincipalTypeEnum),
  principalId: z.string().uuid().optional().nullable(),
  principalKey: z.string().optional().nullable(),
  sourceModuleType: z.nativeEnum(ModuleTypeEnum).optional().nullable(),
  read: z.boolean().optional(),
  update: z.boolean().optional(),
  manage: z.boolean().optional(),
});

export const CustomFieldPermissionConfigSchema = z.object({
  visibleToAllRecordReaders: z.boolean().default(true),
  targets: z.array(CustomFieldPermissionTargetSchema).default([]),
});

export type CustomFieldPermissionTarget = z.infer<typeof CustomFieldPermissionTargetSchema>;
export type CustomFieldPermissionConfig = z.infer<typeof CustomFieldPermissionConfigSchema>;

// Base schema for CustomFieldDefinition, matching Prisma model
export const CustomFieldDefinitionSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  targetModule: z.nativeEnum(ModuleTypeEnum).nullable().optional(), // Optional and nullable
  // Alias for targetModule used in frontend components
  moduleType: z.nativeEnum(ModuleTypeEnum).nullable().optional(),
  sourceField: z.string().nullable().optional(),
  fieldName: z.string(),
  label: z.string(),
  fieldType: FieldTypeEnum,
  isActive: z.boolean().default(true),
  isStandard: z.boolean().default(false),
  // Universal fields apply to all modules (when targetModule is null and this is true)
  isUniversal: z.boolean().default(false).optional(),
  settings: z.record(z.string(), z.any()).default({}).optional(), // JSON field, represented as any object
  permissionConfig: CustomFieldPermissionConfigSchema.optional(),
  order: z.number().int().default(0),
  collectionId: z.string().uuid().nullable().optional(),
  collectionScopeId: z.string().uuid().nullable().optional(),
  userId: z.string().uuid().nullable().optional(), // null = org-wide, set = user-specific
  projectId: z.string().uuid().nullable().optional(), // null = org-wide, set = project-specific
  isManaged: z.boolean().default(false),
  relationshipDefinitionId: z.string().uuid().nullable().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
});
export type CustomFieldDefinition = z.infer<typeof CustomFieldDefinitionSchema>;

// Schema for creating a CustomFieldDefinition
export const CreateCustomFieldDefinitionDtoSchema = CustomFieldDefinitionSchema.omit({
  id: true,
  organizationId: true, // Will be injected by the backend based on context
  createdAt: true,
  updatedAt: true,
}).extend({
  // Ensure targetModule is optional for creation if it can be null
  targetModule: z.nativeEnum(ModuleTypeEnum).optional().nullable(),
  // Order is optional on create: when omitted, the backend appends the field to
  // the end of its scope. (A `.default(0)` here would coerce a missing order to
  // 0, sorting the new column ahead of existing ones instead of at the end.)
  order: z.number().int().optional(),
  collectionScopeId: z.string().uuid().optional().nullable(),
  // userId will be injected by backend based on context (null for org-wide, user ID for user-specific)
  userId: z.string().uuid().optional().nullable(),
  // projectId will be set based on context (null for org-wide, project ID for project-specific)
  projectId: z.string().uuid().optional().nullable(),
});
export type CreateCustomFieldDefinitionDto = z.infer<typeof CreateCustomFieldDefinitionDtoSchema>;

// Schema for updating a CustomFieldDefinition
export const UpdateCustomFieldDefinitionDtoSchema = CreateCustomFieldDefinitionDtoSchema.partial();
export type UpdateCustomFieldDefinitionDto = z.infer<typeof UpdateCustomFieldDefinitionDtoSchema>;

// Schema for CustomFieldValue - useful for when we build that out
export const CustomFieldValueSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  moduleType: z.nativeEnum(ModuleTypeEnum),
  moduleId: z.string().uuid(),
  fieldId: z.string().uuid(),
  value: z.any(), // JSON field, can be any type based on fieldType of definition
  createdAt: z.date(),
  updatedAt: z.date(),
});
export type CustomFieldValue = z.infer<typeof CustomFieldValueSchema>; 
